import {
  DataTable,
  Dialog,
  FormInput,
  FormLabel,
  FormSelect,
  Heading,
  Loader,
} from "@/components";
import useInvoiceListing from "./useInvoiceListing";
import { Button, Textarea } from "@/components/ui";
import { invoiceParkStatus } from "@/lib/utils";
import { FormProvider } from "react-hook-form";
import {
  parkDocumentSearch,
  postDocumentSearch,
  SEARCH_CRITERIA,
  SEARCH_TYPE,
  searchType,
} from "./consts";

const InvoiceListing = () => {
  const {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    onRowClick,
    invoiceAction,
    isDocLoading,
    documentList,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    isDocRefetching,
    form,
    onSubmit,
    requiredFields,
    onClear,
    openReverseDialog,
    setOpenReverseDialog,
    onReverseInvoice,
    onCommentChange,
    isPosted,
    isReversingPostedDoc,
    showLinkPopup,
    setShowLinkPopup,
    viewPaymentDoc,
    clearingId,
    viewReversalDoc,
    reversalId,
    isFromPaymentListing,
    onBackToPaymentList,
    currentFinanceYear
  } = useInvoiceListing();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6">
      {/* Header & Search */}
      <div className="flex flex-col xl:flex-row gap-2 xl:items-center xl:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading
            title={<span>
              Invoice Document Management {
                invoiceParkStatus[invoiceAction || ""]
                  ? "- " + invoiceParkStatus[invoiceAction || ""]?.label
                  : ""
              }
              <span className="text-[12px]">(FY-{currentFinanceYear})</span>
            </span>}
            breadcrumbData={breadcrumbData}
            showBack={isFromPaymentListing}
            onBackClick={onBackToPaymentList}
          />
          {/* {isMobile && <Button className="px-2">+ Add Budget</Button>} */}
        </div>
      </div>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px] mb-[30px]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              <FormSelect
                name={SEARCH_TYPE}
                control={form.control}
                label="Search Type"
                placeholder="Please select"
                error={errors[SEARCH_TYPE]?.message}
                isRequired={requiredFields.includes(SEARCH_TYPE)}
                options={
                  isPosted
                    ? [...postDocumentSearch, ...searchType]
                    : [...parkDocumentSearch, ...searchType]
                }
              />
              <FormInput
                name={SEARCH_CRITERIA}
                control={form.control}
                label="Search Criteria"
                placeholder="Please enter"
                error={errors[SEARCH_CRITERIA]?.message}
                isRequired={requiredFields.includes(SEARCH_CRITERIA)}
              />
              <div className="flex flex-row justify-end gap-3 pt-3">
                <Button type="submit">Search</Button>
                <Button variant="outline" type="button" onClick={onClear}>
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>

      {/* Responsive Table */}
      {isDocLoading || isDocRefetching ? (
        <Loader />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={documentList?.data || []}
            setGlobalFilter={setGlobalFilter}
            globalFilter={globalFilter}
            onRowClick={onRowClick}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalPages={Math.ceil(documentList?.total / pageSize)}
            totalRecords={documentList?.total}
            serverSidePagination={true}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        </>
      )}
      {openReverseDialog && (
        <Dialog
          open={openReverseDialog}
          setOpen={setOpenReverseDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Reverse Document Confirmation!"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          saveText="Yes"
          onSave={onReverseInvoice}
          contentClassName="md:w-[800px]"
          isLoading={isReversingPostedDoc}
        >
          <span className="text-[14px]">
            You are about to reverse this invoice document. <br />
            All associated line items, GL postings, and budget consumption
            records will be reversed. This action is irreversible.
            <br />
            <br /> Please provide a reason for the reversal in the comments box
            below. <br />
            <br />
          </span>
          <div className="relative">
            <FormLabel>
              Comment <span className="text-red-600">*</span>
            </FormLabel>
            <Textarea placeholder="Please enter" onChange={onCommentChange} />
          </div>{" "}
          <br />
          <b>Are you sure you want to proceed?</b>
        </Dialog>
      )}

      {showLinkPopup && (
        <Dialog
          open={showLinkPopup}
          setOpen={setShowLinkPopup}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Linked Document"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          hideFooter
          contentClassName="md:w-[800px]"
        >
          {reversalId || clearingId ? (
            <>
              <div className="text-[14px] font-[400]">
                Select a document below to view its details.
              </div>
              <div className="grid grid-cols-2 gap-6 mt-6">
                {reversalId ? (
                  <Button variant="outline" onClick={viewReversalDoc}>
                    Reverse Document
                  </Button>
                ) : (
                  <></>
                )}
                {clearingId ? (
                  <Button onClick={viewPaymentDoc}>Payment Document</Button>
                ) : (
                  <></>
                )}
              </div>
            </>
          ) : (
            <div>No Linked Document Found</div>
          )}
        </Dialog>
      )}
    </div>
  );
};

export default InvoiceListing;
